import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { Radar as RadarIcon, Sparkles, CalendarClock, TrendingDown, AlertTriangle, Gauge } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtNum, fmtMoney, fmtDate } from "@/lib/format";
import { Kpi, Panel, Badge2, Bar, PageHead } from "@/components/Bits";
import { AiLocked, useTrial } from "@/components/TrialGuard";
import { Button } from "@/components/ui/button";

const BAND_TONE = { red: "bad", amber: "warn", green: "good" };
const BAND_DOT = { red: "bg-red-500", amber: "bg-amber-500", green: "bg-emerald-500" };

const Brief = ({ text }) => (
  <div className="space-y-1.5">
    {text.split("\n").filter(Boolean).map((line, i) => {
      const t = line.trim();
      if (t.startsWith("## ")) return <h4 key={i} className="text-[12px] uppercase tracking-wider text-[#F97316] font-semibold mt-3 first:mt-0">{t.slice(3)}</h4>;
      if (t.startsWith("#")) return <h4 key={i} className="font-semibold">{t.replace(/^#+\s*/, "")}</h4>;
      if (/^[-*•]\s/.test(t)) return <div key={i} className="flex gap-2 text-[13px] leading-relaxed"><span className="text-[#F97316]">·</span><span>{t.replace(/^[-*•]\s/, "").replace(/\*\*/g, "")}</span></div>;
      return <p key={i} className="text-[13px] leading-relaxed">{t.replace(/\*\*/g, "")}</p>;
    })}
  </div>
);

export default function RiskRadar() {
  const { pid } = useParams();
  const { status } = useTrial();
  const aiLocked = status && !status.ai_unlocked;
  const [d, setD] = useState(null);
  const [brief, setBrief] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (aiLocked) return;
    api.get(`/projects/${pid}/risk`).then(({ data }) => setD(data));
    api.get(`/projects/${pid}/risk/narrative`).then(({ data }) => setBrief(data?.text ? data : null));
  }, [pid, aiLocked]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${pid}/risk/narrative`, {});
      setBrief({ text: data.narrative, generated_on: data.generated_on, model: data.model });
      toast.success("AI risk brief generated");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (aiLocked) return <div data-testid="risk-page"><AiLocked feature="Risk Radar" /></div>;
  if (!d) return <div className="text-sm text-muted-foreground">Running predictive model on DPR trend data…</div>;

  const sched = d.schedule;
  const late = (sched.slip_days ?? 0) > 0;

  return (
    <div data-testid="risk-page">
      <PageHead title="Risk Radar — Predictive Analytics" subtitle="Slippage and cost-overrun forecast derived from DPR velocity, recalculated every time the field logs work.">
        <Badge2 tone={BAND_TONE[d.overall_band]} testId="risk-overall-badge">{d.overall_band.toUpperCase()}</Badge2>
        <Badge2 tone="primary">Model confidence {d.confidence_pct}%</Badge2>
        <Button onClick={generate} disabled={busy} data-testid="risk-generate-brief-btn">
          <Sparkles className="w-4 h-4 mr-1.5" /> {busy ? "Analysing…" : "AI risk brief"}
        </Button>
      </PageHead>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi label="Portfolio health" value={`${d.health_score}`} tone={BAND_TONE[d.overall_band]} icon={Gauge} testId="kpi-health-score" sub={`${d.counts.red} red · ${d.counts.amber} amber · ${d.counts.green} green`} />
        <Kpi label="Forecast completion" value={sched.forecast_finish ? fmtDate(sched.forecast_finish) : "—"} tone={late ? "bad" : "good"} icon={CalendarClock} testId="kpi-forecast-date" sub={`Contract ${fmtDate(sched.planned_finish)}`} />
        <Kpi label="Schedule slip" value={sched.slip_days === null ? "—" : `${sched.slip_days > 0 ? "+" : ""}${sched.slip_days} d`} tone={late ? "bad" : "good"} testId="kpi-slip-days" sub={`SPI ${sched.spi} · trend ${sched.spi_trend > 0 ? "+" : ""}${sched.spi_trend}`} />
        <Kpi label="Forecast cost" value={fmtMoney(d.cost.forecast_cost)} tone={d.cost.overrun > 0 ? "bad" : "good"} icon={TrendingDown} testId="kpi-forecast-cost" sub={`BAC ${fmtMoney(d.cost.bac)}`} />
        <Kpi label="Projected overrun" value={`${d.cost.overrun_pct}%`} tone={d.cost.overrun_pct > 0 ? "bad" : "good"} testId="kpi-overrun" sub={fmtMoney(d.cost.overrun)} />
        <Kpi label="Value at risk" value={fmtMoney(d.cost.value_at_risk)} tone="warn" icon={AlertTriangle} sub={`${d.stalled_activities} stalled fronts`} />
      </div>

      <div className="grid xl:grid-cols-3 gap-3 mt-3">
        <Panel title="Performance index trend (8 review periods)" className="xl:col-span-2" testId="risk-trend-panel">
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sched.trend} margin={{ top: 6, right: 12, left: -18 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} domain={[0, "auto"]} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={1} stroke="#F97316" strokeDasharray="4 4" />
                <Line type="monotone" dataKey="spi" name="SPI" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="cpi" name="CPI" stroke="#F97316" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            SPI moved {sched.spi_trend > 0 ? "up" : "down"} {Math.abs(sched.spi_trend)} over the last 8 weeks.
            Forecast completion is recomputed daily from the trailing 21-day run-rate of every work front.
          </p>
        </Panel>

        <Panel title="AI risk brief" testId="risk-brief-panel"
          action={brief ? <span className="text-[10px] text-muted-foreground num">{brief.model}</span> : null}>
          {brief ? (
            <>
              <Brief text={brief.text} />
              <p className="text-[10px] text-muted-foreground mt-3">Generated {fmtDate(brief.generated_on)} from the deterministic model output.</p>
            </>
          ) : (
            <div className="text-sm text-muted-foreground">
              <RadarIcon className="w-6 h-6 text-[#F97316] mb-2" />
              Generate a management-ready brief that explains the RAG verdict, cites the numbers driving it and lists recovery actions.
              <Button className="w-full mt-4" onClick={generate} disabled={busy} data-testid="risk-brief-empty-btn">
                <Sparkles className="w-4 h-4 mr-1.5" /> {busy ? "Analysing…" : "Generate brief"}
              </Button>
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Activity-level risk register (Red / Amber / Green)" className="mt-3" testId="risk-table-panel">
        <div className="overflow-x-auto max-h-[60vh]">
          <table className="w-full dense-table">
            <thead className="sticky-head">
              <tr>
                <th>RAG</th><th>Code</th><th>Activity</th><th>Status</th><th className="text-right">Planned %</th>
                <th className="text-right">Actual %</th><th className="text-right">Slippage</th><th>Progress</th>
                <th className="text-right">Run-rate now</th><th className="text-right">Required</th><th>Trend</th>
                <th>Contract finish</th><th>Forecast finish</th><th className="text-right">Days late</th>
                <th className="text-right">Value at risk</th>
              </tr>
            </thead>
            <tbody>
              {d.activities.map((r) => (
                <tr key={r.id} className={r.risk === "red" ? "bg-red-500/5" : ""} data-testid={`risk-row-${r.code}`}>
                  <td><span className={`inline-block w-2.5 h-2.5 rounded-full ${BAND_DOT[r.risk]}`} title={r.risk} /></td>
                  <td className="num text-primary">{r.code}</td>
                  <td className="max-w-[220px] truncate" title={r.name}>
                    {r.name} {r.critical ? <Badge2 tone="bad">CP</Badge2> : null}
                  </td>
                  <td className="text-muted-foreground">{r.status}</td>
                  <td className="num text-right text-muted-foreground">{r.planned_pct}%</td>
                  <td className="num text-right">{r.actual_pct}%</td>
                  <td className={`num text-right ${r.slippage_pct > 5 ? "text-red-500" : r.slippage_pct > 0 ? "text-amber-500" : "text-emerald-500"}`}>{r.slippage_pct}%</td>
                  <td><Bar pct={r.actual_pct} /></td>
                  <td className="num text-right">{fmtNum(r.recent_velocity, 1)}</td>
                  <td className="num text-right text-muted-foreground">{fmtNum(r.run_rate_required, 1)}</td>
                  <td>
                    <Badge2 tone={r.trend === "accelerating" ? "good" : r.trend === "decelerating" ? "bad" : "muted"}>{r.trend}</Badge2>
                  </td>
                  <td className="num text-[11px] text-muted-foreground">{fmtDate(r.planned_finish)}</td>
                  <td className="num text-[11px]">{r.forecast_finish ? fmtDate(r.forecast_finish) : "no velocity"}</td>
                  <td className={`num text-right ${(r.days_late ?? 0) > 0 ? "text-red-500" : "text-emerald-500"}`}>{r.days_late ?? "—"}</td>
                  <td className="num text-right">{fmtMoney(r.value_at_risk)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
